import { Alert, Box, Button, CircularProgress, FormControl, FormHelperText, ToggleButton, ToggleButtonGroup, TextField, Typography } from '@mui/material';
import { useState } from 'react';

import { ParameterType } from '../../../api/api';
import { ExpressionInput } from '../Components/Expressions/ExpressionInput';
import useAgentToolInputContract from '../Hooks/useAgentToolInputContract';
import { FlowDefinitionModel, FlowEdgeModel } from '../Models/FlowModel';
import { ActivityNode, TransitionEdge } from '../Models/GraphModel';
import { VariableScope } from '../Models/VariableScopes/VariableScope';
import { removeAgentToolInputOverride, updateAgentToolInputOverride } from '../Services/AgentToolInputOverrides';

type Props = {
	transition: TransitionEdge;
	activity: ActivityNode;
	flow: FlowDefinitionModel;
	variableScope?: VariableScope;
	readOnly?: boolean;
	onChange?: (id: string, model: FlowEdgeModel) => void;
};

function ToolFieldDescription({ value, disabled, onCommit }: { value?: string; disabled: boolean; onCommit: (value: string | undefined) => void }) {
	const [draft, setDraft] = useState<string>();
	return (
		<TextField
			fullWidth
			size="small"
			variant="standard"
			label="Tool field description"
			InputLabelProps={{ shrink: true }}
			sx={{ mt: 1 }}
			value={draft ?? value ?? ''}
			disabled={disabled}
			onChange={(event) => setDraft(event.target.value)}
			onBlur={() => {
				if (!disabled && draft !== undefined && draft !== (value ?? '')) {
					onCommit(draft || undefined);
				}
				setDraft(undefined);
			}}
		/>
	);
}

/** Bindings belong to this caller edge, and expressions use the caller's scope. */
export function AgentToolInputBindings({ transition, activity, flow, variableScope, readOnly, onChange }: Props) {
	const contract = useAgentToolInputContract(activity, flow);
	const bindings = transition.data?.InputOverrides ?? [];
	const update = (name: string, change: Parameters<typeof updateAgentToolInputOverride>[3]) => {
		if (readOnly || !transition.data) return;
		onChange?.(transition.id, { ...transition.data, InputOverrides: updateAgentToolInputOverride(bindings, activity.id, name, change) });
	};
	if (contract.status === 'loading') return <CircularProgress size={20} />;
	if (contract.status !== 'ready') return <Alert severity={contract.status === 'error' ? 'error' : 'info'}>{contract.message}</Alert>;
	const fields = contract.fields;
	const stale = bindings.filter(
		(input) => input.ActivityId === activity.id && !fields.some((field) => field.name.toLowerCase() === input.PropertyName.toLowerCase()),
	);
	return (
		<Box sx={{ px: 2, pb: 2 }}>
			<Typography variant="subtitle2">Tool input bindings</Typography>
			<Typography variant="caption" color="text.secondary">
				Applies only to this transition. A bound expression runs in the calling agent's scope and hides the field from the agent, even when it evaluates to
				null.
			</Typography>
			{stale.map((input) => (
				<Alert
					key={input.PropertyName}
					severity="warning"
					sx={{ mt: 1 }}
					action={
						<Button
							disabled={readOnly || !onChange}
							onClick={() => {
								if (readOnly || !transition.data) return;
								onChange?.(transition.id, { ...transition.data, InputOverrides: removeAgentToolInputOverride(bindings, activity.id, input.PropertyName) });
							}}
						>
							Remove binding
						</Button>
					}
				>
					Unknown input: {input.PropertyName}. This saved binding no longer exists in the tool contract.
				</Alert>
			))}
			{fields.length === 0 && <Typography variant="body2">No callable input properties found.</Typography>}
			{fields.map((field) => {
				const binding = bindings.find((input) => input.ActivityId === activity.id && input.PropertyName.toLowerCase() === field.name.toLowerCase());
				return (
					<Box key={field.name} sx={{ mt: 2 }}>
						<Typography variant="body2">{field.displayName}</Typography>
						<Box sx={{ borderLeft: 2, borderColor: 'divider', ml: 0.5, pl: 1.5, mt: 0.5, pb: 0.5, minWidth: 0 }}>
						<FormControl fullWidth size="small" sx={{ mt: 1 }}>
							<Typography id={`${transition.id}-${field.name}-mode`} variant="caption" color="text.secondary">Agent input</Typography>
							<ToggleButtonGroup
								aria-labelledby={`${transition.id}-${field.name}-mode`}
								exclusive
								fullWidth
								size="small"
								disabled={readOnly || !onChange || !!binding?.Override}
								value={binding?.Enabled == null ? 'default' : binding.Enabled ? 'enabled' : 'disabled'}
								onChange={(_, value) => {
									if (value !== null) {
										update(field.name, { Enabled: value === 'default' ? undefined : value === 'enabled' });
									}
								}}
							>
								<ToggleButton value="default">Default</ToggleButton>
								<ToggleButton value="enabled">Enabled</ToggleButton>
								<ToggleButton value="disabled">Disabled</ToggleButton>
							</ToggleButtonGroup>
							<FormHelperText>{binding?.Override ? 'Flow-controlled expression takes precedence over this setting.' : field.description}</FormHelperText>
						</FormControl>
						<Box sx={{ mt: 1, minWidth: 0 }}>
							<Typography variant="caption">Override expression (optional)</Typography>
							<ExpressionInput
								expression={binding?.Override}
								onExpressionChange={(Override) => update(field.name, { Override })}
								parameterMetadata={{ ...field, type: ParameterType.Expression }}
								variableScope={variableScope}
								disabled={readOnly || !onChange}
							/>
						</Box>
						<ToolFieldDescription
							key={`${transition.id}-${activity.id}-${field.name}`}
							value={binding?.Description ?? ''}
							disabled={!!readOnly || !onChange}
							onCommit={(Description) => update(field.name, { Description })}
						/>
						</Box>
					</Box>
				);
			})}
		</Box>
	);
}
